import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Home, Building2, Briefcase, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PasswordInput, Field } from "@/components/common";
import { AuthShell, GoogleButton, Divider } from "@/pages/Login";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { api, errMsg, dashboardPath } from "@/lib/api";

export const RolePicker = ({ value, onChange }) => {
  const { t } = useLang();
  const roles = [["tenant", Briefcase], ["owner", Home], ["agency", Building2]];
  return (
    <div className="grid gap-2" data-testid="role-picker">
      {roles.map(([r, I]) => (
        <button type="button" key={r} onClick={() => onChange(r)} data-testid={`role-${r}-btn`}
          className={`flex items-center gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${value === r ? "border-[#FF6B00] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
          <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${value === r ? "bg-[#FF6B00] text-white" : "bg-slate-100 text-[#142B5C]"}`}><I className="h-5 w-5" /></span>
          <span className="flex-1"><span className="block font-bold text-[#0B192C]">{t(`r_${r}`)}</span><span className="block text-xs text-slate-500">{t(`r_${r}_d`)}</span></span>
          {value === r && <Check className="h-5 w-5 text-[#FF6B00]" />}
        </button>
      ))}
    </div>
  );
};

export default function Register() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ role: sp.get("role") || "tenant", name: "", company_name: "", vat_number: "", phone: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", f);
      setUser(data);
      nav(dashboardPath(data));
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title={t("register_title")} sub={t("register_sub")} testid="register-page">
      <form onSubmit={submit} className="grid gap-4">
        <RolePicker value={f.role} onChange={(role) => setF({ ...f, role })} />
        <Field label={t("name")}><Input required value={f.name} onChange={set("name")} className="h-12 rounded-xl" data-testid="register-name-input" /></Field>
        {f.role !== "owner" && <Field label={t("company")}><Input required={f.role === "agency"} value={f.company_name} onChange={set("company_name")} className="h-12 rounded-xl" data-testid="register-company-input" /></Field>}
        {f.role === "agency" && <Field label={t("vat")}><Input required value={f.vat_number} onChange={set("vat_number")} className="h-12 rounded-xl" data-testid="register-vat-input" /></Field>}
        <Field label={t("phone")}><Input type="tel" value={f.phone} onChange={set("phone")} className="h-12 rounded-xl" data-testid="register-phone-input" /></Field>
        <Field label={t("email")}><Input type="email" required value={f.email} onChange={set("email")} className="h-12 rounded-xl" data-testid="register-email-input" /></Field>
        <Field label={t("password")} hint={t("pw_hint")}><PasswordInput required minLength={8} value={f.password} onChange={set("password")} testid="register-password-input" /></Field>
        {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
        <button disabled={busy} className="h-12 rounded-full bg-[#FF6B00] font-bold text-white hover:bg-[#E05A00] disabled:opacity-60" data-testid="register-submit-btn">{t("register_btn")}</button>
      </form>
      <Divider />
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-slate-600">{t("have_account")} <Link to="/login" className="font-bold text-[#142B5C]" data-testid="go-login-link">{t("nav_login")}</Link></p>
    </AuthShell>
  );
}
